import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { forgotPassword } from "../lib/api";

/** /forgot — self-serve password reset. The backend always returns a
 * generic message (no account enumeration) and delivers the link by
 * email only, so this page never reveals whether an account exists.
 * If email delivery isn't configured yet, an admin can issue a reset
 * with a copy-link from the admin pages instead. */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      await forgotPassword(email.trim());
    } catch {
      // Intentionally ignore — the endpoint is always-200; any error
      // shouldn't reveal anything. Show the same confirmation.
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center px-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/agrilens-background.jpg')" }}
    >
      <div className="absolute inset-0 bg-white/25" aria-hidden="true" />
      <div className="relative z-10 w-full max-w-sm bg-white/95 backdrop-blur-sm shadow-xl rounded-lg p-6 space-y-4">
        <div className="flex justify-center pb-1">
          <img src="/logo-app.png" alt="AgriLens.AI" className="h-16 w-auto" />
        </div>
        {done ? (
          <>
            <h1 className="text-xl font-semibold text-agrilens-green-dark text-center">
              Check your email
            </h1>
            <p className="text-sm text-gray-600 text-center">
              If that address has an account, a password-reset link is on
              its way. It expires in 24 hours.
            </p>
            <Link
              to="/login"
              className="block text-center text-sm text-agrilens-green hover:underline"
            >
              Back to sign in
            </Link>
          </>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <h1 className="text-xl font-semibold text-agrilens-green-dark text-center">
              Reset your password
            </h1>
            <p className="text-sm text-gray-600 text-center">
              Enter your email and we'll send a reset link.
            </p>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border rounded px-3 py-2"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-agrilens-green text-white py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </button>
            <Link
              to="/login"
              className="block text-center text-sm text-agrilens-green hover:underline"
            >
              Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
